import { Component, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n/I18nProvider";

// Core chrome loaded eagerly to avoid layout flashes / context loss.
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import ServiceAgentWidget from "@/components/ServiceAgentWidget";
import ProtectedRoute from "@/components/ProtectedRoute";
import SeoHead from "@/components/SeoHead";
import Analytics from "@/lib/analytics";
import { RedirectCity, RedirectState, RedirectCompare, RedirectCompareIndex } from "@/components/Redirects";
import { RecentBookingToast, ScrollLeadCard } from "@/components/LeadFunnel";

// ---------------------------------------------------------------------------
// Page-level code-splitting. Each page is a separate JS chunk that only loads
// when the user navigates to it. This slashes the initial bundle (~10 MB → a
// few hundred KB) and fixes the "blank screen on first load" issue.
//
// `lazyRetry` wraps React.lazy with automatic retry + hard-reload recovery
// for the well-known SPA failure mode where a chunk fetch fails transiently
// (CDN hiccup, ISP proxy, ad-blocker, stale hashed name after redeploy etc.)
// and the user is stranded on a blank white screen. We retry twice with
// backoff. If all retries still fail AND we haven't already tried once this
// session, force a full page reload so the browser fetches a fresh
// index.html referring to the current chunk hashes.
// ---------------------------------------------------------------------------
const CHUNK_RELOAD_KEY = "si_chunk_reload_attempted";

function retryImport(importer, retries) {
  return importer().catch((err) => {
    if (retries <= 0) {
      // Final failure — attempt one full-page recovery reload, then rethrow.
      try {
        if (typeof window !== "undefined" &&
            !window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          window.sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
          window.location.reload();
        }
      } catch (_) { /* storage may be blocked in incognito */ }
      throw err;
    }
    return new Promise((resolve, reject) =>
      setTimeout(() => retryImport(importer, retries - 1).then(resolve, reject), 400)
    );
  });
}
const lazyRetry = (importer) => lazy(() => retryImport(importer, 2));

// Clear the reload flag once the app successfully mounts, so future genuine
// chunk failures can trigger recovery again on the next navigation.
if (typeof window !== "undefined") {
  window.addEventListener("load", () => {
    try { window.sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch (_) { /* ignore */ }
  });
}

// Public pages
const Home = lazyRetry(() => import("@/pages/Home"));
const Products = lazyRetry(() => import("@/pages/Products"));
const Cities = lazyRetry(() => import("@/pages/Cities"));
const CityDetail = lazyRetry(() => import("@/pages/CityDetail"));
const StateDetail = lazyRetry(() => import("@/pages/StateDetail"));
const Blog = lazyRetry(() => import("@/pages/Blog"));
const BlogDetail = lazyRetry(() => import("@/pages/BlogDetail"));
const BlogTag = lazyRetry(() => import("@/pages/BlogTag"));
const About = lazyRetry(() => import("@/pages/About"));
const Contact = lazyRetry(() => import("@/pages/Contact"));
const Branches = lazyRetry(() => import("@/pages/Branches"));
const InstallationDetail = lazyRetry(() => import("@/pages/InstallationDetail"));
const CategoryPillar = lazyRetry(() => import("@/pages/CategoryPillar"));
const BoardComparator = lazyRetry(() => import("@/pages/BoardComparator"));
const StudioSetup = lazyRetry(() => import("@/pages/StudioSetup"));
const AcousticCalculator = lazyRetry(() => import("@/pages/AcousticCalculator"));
const AIRecommendation = lazyRetry(() => import("@/pages/AIRecommendation"));
const Quotation = lazyRetry(() => import("@/pages/Quotation"));
const CitySegmentLanding = lazyRetry(() => import("@/pages/CitySegmentLanding"));
const BranchLanding = lazyRetry(() => import("@/pages/BranchLanding"));
const InstallationTimeline = lazyRetry(() => import("@/pages/InstallationTimeline"));
const IndiaHeatMap = lazyRetry(() => import("@/pages/IndiaHeatMap"));
const CoachingsList = lazyRetry(() => import("@/pages/CoachingsList"));
const CoachingDetail = lazyRetry(() => import("@/pages/CoachingDetail"));
const EducatorsList = lazyRetry(() => import("@/pages/EducatorsList"));
const EducatorDetail = lazyRetry(() => import("@/pages/EducatorDetail"));
const DemoBooking = lazyRetry(() => import("@/pages/DemoBooking"));
const StudioExperience = lazyRetry(() => import("@/pages/StudioExperience"));
const FreeResources = lazyRetry(() => import("@/pages/FreeResources"));

// Compare (named exports — wrap into lazy chunks)
const CompareListPage = lazyRetry(() =>
  import("@/pages/Compare").then((m) => ({ default: m.CompareListPage }))
);
const CompareDetailPage = lazyRetry(() =>
  import("@/pages/Compare").then((m) => ({ default: m.CompareDetailPage }))
);

// Admin pages
const AdminLogin = lazyRetry(() => import("@/pages/admin/Login"));
const AdminDashboard = lazyRetry(() => import("@/pages/admin/Dashboard"));
const AdminBlogList = lazyRetry(() => import("@/pages/admin/BlogList"));
const AdminBlogEditor = lazyRetry(() => import("@/pages/admin/BlogEditor"));
const AdminLeads = lazyRetry(() => import("@/pages/admin/Leads"));
const AdminLeadEmailRoutes = lazyRetry(() => import("@/pages/admin/LeadEmailRoutes"));
const AdminStudioPackages = lazyRetry(() => import("@/pages/admin/StudioPackages"));
const AdminStudioPackagesV2 = lazyRetry(() => import("@/pages/admin/StudioPackagesV2Admin"));
const AdminAI = lazyRetry(() => import("@/pages/admin/AIGenerator"));
const AdminBulkAI = lazyRetry(() => import("@/pages/admin/BulkAI"));
const AdminSettings = lazyRetry(() => import("@/pages/admin/Settings"));
const AdminInstallations = lazyRetry(() => import("@/pages/admin/Installations"));
const AdminInstallationAIDashboard = lazyRetry(() => import("@/pages/admin/InstallationAIDashboard"));
const AdminInstallationAIUpload = lazyRetry(() => import("@/pages/admin/InstallationAIUpload"));
const AdminInstallationAIBulkUpload = lazyRetry(() => import("@/pages/admin/InstallationAIBulkUpload"));
const AdminInstallationAIDriveSync = lazyRetry(() => import("@/pages/admin/InstallationAIDriveSync"));
const AdminInstallationAIReview = lazyRetry(() => import("@/pages/admin/InstallationAIReview"));
const AdminInstallationAIAnalytics = lazyRetry(() => import("@/pages/admin/InstallationAIAnalytics"));
const AdminActivityLogs = lazyRetry(() => import("@/pages/admin/ActivityLogs"));
const AdminDesignStudio = lazyRetry(() => import("@/pages/admin/DesignStudio"));
const AdminDesignStudioMasters = lazyRetry(() => import("@/pages/admin/DesignStudioMasters"));
const AdminQuotationsCRM = lazyRetry(() => import("@/pages/admin/QuotationsCRM"));
const AdminVideos = lazyRetry(() => import("@/pages/admin/Videos"));
const AdminShopify = lazyRetry(() => import("@/pages/admin/Shopify"));
const AdminPageBanners = lazyRetry(() => import("@/pages/admin/PageBanners"));
const AdminExperienceCenters = lazyRetry(() => import("@/pages/admin/ExperienceCenters"));
const AdminBenchmarkCatalog = lazyRetry(() => import("@/pages/admin/BenchmarkCatalog"));
const AdminSalesScripts = lazyRetry(() => import("@/pages/admin/SalesScripts"));
const AdminSalesConsole = lazyRetry(() => import("@/pages/admin/SalesConsole"));
const AdminSalesReps = lazyRetry(() => import("@/pages/admin/SalesReps"));
const AdminSeoTools = lazyRetry(() => import("@/pages/admin/SeoTools"));
const AdminBulkTestimonials = lazyRetry(() => import("@/pages/admin/BulkTestimonials"));
const AdminAssessment = lazyRetry(() => import("@/pages/admin/AssessmentAdmin"));
const AdminAssessmentSubmissions = lazyRetry(() => import("@/pages/admin/AssessmentSubmissionsAdmin"));
const AdminLeadMagnets = lazyRetry(() => import("@/pages/admin/LeadMagnetsAdmin"));
const AdminAIKnowledge = lazyRetry(() => import("@/pages/admin/AIKnowledgeAdmin"));

// Sales rep pages
const SalesLogin = lazyRetry(() => import("@/pages/sales/SalesLogin"));
const SalesConsoleRep = lazyRetry(() => import("@/pages/sales/SalesConsoleRep"));
const SalesHistory = lazyRetry(() => import("@/pages/sales/SalesHistory"));
const SalesMyLeads = lazyRetry(() => import("@/pages/sales/SalesMyLeads"));

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PublicShell({ children }) {
  return (
    <>
      <Navbar />
      <main className="min-h-[60vh]">{children}</main>
      <Footer />
      <FloatingActions />
      <ExitIntentPopup />
      <ServiceAgentWidget />
      <RecentBookingToast />
      <ScrollLeadCard sourcePage={typeof window !== "undefined" ? window.location.pathname : "unknown"} />
    </>
  );
}

// Apple-style minimal loader shown while a lazy-loaded route chunk is being
// fetched. Visually matches the inline HTML loader in public/index.html so
// there is no visual gap between HTML paint → React hydration → chunk fetch.
function RouteFallback() {
  return (
    <div
      data-testid="route-suspense-fallback"
      className="fixed inset-0 flex flex-col items-center justify-center bg-white z-[1]"
    >
      <img
        src="/logo.png"
        alt="Smart Infovision"
        className="w-[120px] h-auto opacity-90 select-none"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
      <div className="mt-6 w-7 h-7 rounded-full border-2 border-slate-200 border-t-slate-900 animate-spin" />
      <p className="mt-3.5 text-xs text-slate-500 tracking-wide">Loading…</p>
    </div>
  );
}

// Error boundary that catches unhandled render errors — most importantly
// chunk-load failures thrown by React.lazy when the network / CDN fails to
// serve a route chunk. Instead of leaving the user on a blank white screen,
// we show a friendly recovery UI with a manual reload button.
class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) {
    console.error("[ChunkErrorBoundary]", err);
  }
  handleReload = () => {
    try { window.sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch (_) { /* ignore */ }
    window.location.reload();
  };
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="chunk-error-boundary"
        className="fixed inset-0 flex flex-col items-center justify-center bg-white px-6 text-center"
      >
        <img
          src="/logo.png"
          alt="Smart Infovision"
          className="w-[120px] h-auto opacity-90 select-none mb-8"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <h1 className="text-lg font-semibold text-slate-900 tracking-tight">
          Couldn’t load this page
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-md">
          Your connection may have dropped or a new version of the site is
          available. Please reload to continue.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          className="mt-6 h-11 px-6 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
          data-testid="chunk-error-reload-btn"
        >
          Reload
        </button>
      </div>
    );
  }
}

export default function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <BrowserRouter>
          <ScrollTop />
          <Analytics />
          <SeoHead />
          <ChunkErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
              {/* Admin routes — no public chrome */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/blogs" element={<ProtectedRoute><AdminBlogList /></ProtectedRoute>} />
              <Route path="/admin/blogs/new" element={<ProtectedRoute><AdminBlogEditor /></ProtectedRoute>} />
              <Route path="/admin/blogs/:id/edit" element={<ProtectedRoute><AdminBlogEditor /></ProtectedRoute>} />
              <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
              <Route path="/admin/lead-email-routes" element={<ProtectedRoute><AdminLeadEmailRoutes /></ProtectedRoute>} />
              <Route path="/admin/studio-packages" element={<ProtectedRoute><AdminStudioPackages /></ProtectedRoute>} />
              <Route path="/admin/studio-packages-v2" element={<ProtectedRoute><AdminStudioPackagesV2 /></ProtectedRoute>} />
              <Route path="/admin/installations" element={<ProtectedRoute><AdminInstallations /></ProtectedRoute>} />
              <Route path="/admin/installation-ai" element={<ProtectedRoute><AdminInstallationAIDashboard /></ProtectedRoute>} />
              <Route path="/admin/installation-ai/upload" element={<ProtectedRoute><AdminInstallationAIUpload /></ProtectedRoute>} />
              <Route path="/admin/installation-ai/bulk-upload" element={<ProtectedRoute><AdminInstallationAIBulkUpload /></ProtectedRoute>} />
              <Route path="/admin/installation-ai/drive-sync" element={<ProtectedRoute><AdminInstallationAIDriveSync /></ProtectedRoute>} />
              <Route path="/admin/installation-ai/review/:id" element={<ProtectedRoute><AdminInstallationAIReview /></ProtectedRoute>} />
              <Route path="/admin/installation-ai/analytics" element={<ProtectedRoute><AdminInstallationAIAnalytics /></ProtectedRoute>} />
              <Route path="/admin/activity-logs" element={<ProtectedRoute><AdminActivityLogs /></ProtectedRoute>} />
              <Route path="/admin/design-studio" element={<ProtectedRoute><AdminDesignStudio /></ProtectedRoute>} />
              <Route path="/admin/design-studio/masters" element={<ProtectedRoute><AdminDesignStudioMasters /></ProtectedRoute>} />
              <Route path="/installations/timeline" element={<PublicShell><InstallationTimeline /></PublicShell>} />
              <Route path="/installations/timeline/:year" element={<PublicShell><InstallationTimeline /></PublicShell>} />
              <Route path="/coverage-map" element={<PublicShell><IndiaHeatMap /></PublicShell>} />
              <Route path="/coachings" element={<PublicShell><CoachingsList /></PublicShell>} />
              <Route path="/coachings/:slug" element={<PublicShell><CoachingDetail /></PublicShell>} />
              <Route path="/educators" element={<PublicShell><EducatorsList /></PublicShell>} />
              <Route path="/educators/:slug" element={<PublicShell><EducatorDetail /></PublicShell>} />
              <Route path="/admin/videos" element={<ProtectedRoute><AdminVideos /></ProtectedRoute>} />
              <Route path="/admin/shopify" element={<ProtectedRoute><AdminShopify /></ProtectedRoute>} />
              <Route path="/admin/ai" element={<ProtectedRoute><AdminAI /></ProtectedRoute>} />
              <Route path="/admin/ai/bulk" element={<ProtectedRoute><AdminBulkAI /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
              <Route path="/admin/quotations" element={<ProtectedRoute><AdminQuotationsCRM /></ProtectedRoute>} />
              <Route path="/admin/page-banners" element={<ProtectedRoute><AdminPageBanners /></ProtectedRoute>} />
              <Route path="/admin/experience-centers" element={<ProtectedRoute><AdminExperienceCenters /></ProtectedRoute>} />
              <Route path="/admin/benchmark-catalog" element={<ProtectedRoute><AdminBenchmarkCatalog /></ProtectedRoute>} />
              <Route path="/admin/sales-scripts" element={<ProtectedRoute><AdminSalesScripts /></ProtectedRoute>} />
              <Route path="/admin/sales-console" element={<ProtectedRoute><AdminSalesConsole /></ProtectedRoute>} />
              <Route path="/admin/sales-reps" element={<ProtectedRoute><AdminSalesReps /></ProtectedRoute>} />

              {/* Sales rep public-ish routes */}
              <Route path="/sales/login"   element={<SalesLogin />} />
              <Route path="/sales/console" element={<SalesConsoleRep />} />
              <Route path="/sales/history" element={<SalesHistory />} />
              <Route path="/sales/leads"   element={<SalesMyLeads />} />

              {/* Public comparison pages */}
              <Route path="/compare"        element={<PublicShell><CompareListPage /></PublicShell>} />
              <Route path="/studio-experience" element={<PublicShell><StudioExperience /></PublicShell>} />

              {/* Per-branch landing pages — explicit routes (RR v6 needs literal paths). */}
              {["noida","lucknow","patna","jaunpur","pune","indore","bhagalpur","varanasi","jaipur","ahmedabad","ranchi","mumbai","kolkata","muzaffarpur","gaya","purnia","siliguri"].map((c) => (
                <Route key={c} path={`/smart-infovision-${c}`} element={<PublicShell><BranchLanding /></PublicShell>} />
              ))}

              <Route path="/studio"            element={<PublicShell><StudioExperience /></PublicShell>} />
              <Route path="/compare/:slug"  element={<PublicShell><CompareDetailPage /></PublicShell>} />

              {/* New admin tools */}
              <Route path="/admin/seo-tools" element={<ProtectedRoute><AdminSeoTools /></ProtectedRoute>} />
              <Route path="/admin/bulk-testimonials" element={<ProtectedRoute><AdminBulkTestimonials /></ProtectedRoute>} />
              <Route path="/admin/assessment" element={<ProtectedRoute><AdminAssessment /></ProtectedRoute>} />
              <Route path="/admin/assessment-submissions" element={<ProtectedRoute><AdminAssessmentSubmissions /></ProtectedRoute>} />
              <Route path="/admin/lead-magnets" element={<ProtectedRoute><AdminLeadMagnets /></ProtectedRoute>} />
              <Route path="/admin/ai-knowledge" element={<ProtectedRoute><AdminAIKnowledge /></ProtectedRoute>} />
              <Route path="/demo-booking" element={<PublicShell><DemoBooking /></PublicShell>} />
              <Route path="/free-resources" element={<PublicShell><FreeResources /></PublicShell>} />

              {/* Public routes */}
              <Route path="/" element={<PublicShell><Home /></PublicShell>} />
              <Route path="/products" element={<PublicShell><Products /></PublicShell>} />
              <Route path="/cities" element={<PublicShell><Cities /></PublicShell>} />

              {/* Keyword-rich SEO URLs (canonical). */}
              <Route path="/digital-board-in/:slug" element={<PublicShell><CityDetail /></PublicShell>} />
              <Route path="/smart-board-in/:slug" element={<PublicShell><CityDetail /></PublicShell>} />
              <Route path="/interactive-flat-panel-in/:slug" element={<PublicShell><CityDetail /></PublicShell>} />
              <Route path="/interactive-panel-in/:slug" element={<PublicShell><CityDetail /></PublicShell>} />
              <Route path="/smart-classroom-in/:slug" element={<PublicShell><CityDetail /></PublicShell>} />
              <Route path="/online-teaching-setup-in/:slug" element={<PublicShell><CityDetail /></PublicShell>} />
              <Route path="/digital-board-dealer-in/:slug" element={<PublicShell><StateDetail /></PublicShell>} />
              <Route path="/smart-board-dealer-in/:slug" element={<PublicShell><StateDetail /></PublicShell>} />
              <Route path="/interactive-flat-panel-dealer-in/:slug" element={<PublicShell><StateDetail /></PublicShell>} />

              {/* SEO silo pillar pages */}
              <Route path="/digital-board" element={<PublicShell><CategoryPillar /></PublicShell>} />
              <Route path="/smart-board" element={<PublicShell><CategoryPillar /></PublicShell>} />
              <Route path="/interactive-flat-panel" element={<PublicShell><CategoryPillar /></PublicShell>} />
              <Route path="/smart-classroom" element={<PublicShell><CategoryPillar /></PublicShell>} />
              <Route path="/online-teaching-setup" element={<PublicShell><CategoryPillar /></PublicShell>} />

              <Route path="/board-comparator" element={<PublicShell><BoardComparator /></PublicShell>} />
              <Route path="/studio-setup" element={<PublicShell><StudioSetup /></PublicShell>} />
              <Route path="/acoustic-calculator" element={<PublicShell><AcousticCalculator /></PublicShell>} />
              <Route path="/recommendation" element={<PublicShell><AIRecommendation /></PublicShell>} />
              <Route path="/quotation" element={<PublicShell><Quotation /></PublicShell>} />

              {/* City × Segment SEO landing pages (programmatic). */}
              {["digital-board","smart-board","interactive-flat-panel","smart-classroom"].flatMap((kw) =>
                ["school","coaching","studio","youtuber","corporate"].map((seg) => (
                  <Route
                    key={`${kw}-${seg}`}
                    path={`/${kw}-for-${seg}-in/:citySlug`}
                    element={<PublicShell><CitySegmentLanding fixedKeyword={kw} fixedSegment={seg} /></PublicShell>}
                  />
                ))
              )}

              {/* Old /compare/* URLs redirect to the blog post — comparisons live as blogs now */}
              <Route path="/compare" element={<PublicShell><RedirectCompareIndex /></PublicShell>} />
              <Route path="/compare/:slug" element={<PublicShell><RedirectCompare /></PublicShell>} />

              {/* Backwards-compat — old URLs redirect to the new keyword-rich ones */}
              <Route path="/cities/:slug" element={<PublicShell><RedirectCity /></PublicShell>} />
              <Route path="/states/:slug" element={<PublicShell><RedirectState /></PublicShell>} />

              <Route path="/blog" element={<PublicShell><Blog /></PublicShell>} />
              <Route path="/blog/tag/:tag" element={<PublicShell><BlogTag /></PublicShell>} />
              <Route path="/blog/:slug" element={<PublicShell><BlogDetail /></PublicShell>} />
              <Route path="/about" element={<PublicShell><About /></PublicShell>} />
              <Route path="/branches" element={<PublicShell><Branches /></PublicShell>} />
              <Route path="/installations/:id" element={<PublicShell><InstallationDetail /></PublicShell>} />
              <Route path="/contact" element={<PublicShell><Contact /></PublicShell>} />
            </Routes>
            </Suspense>
          </ChunkErrorBoundary>
          <Toaster position="top-right" />
        </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}
